// src/components/doctor/UpdateAppointmentModal.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X, Calendar, Clock, Check } from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import AppointmentService from '../../services/appoinment.service';
import DoctorCalendarService from '../../services/doctor.calendar.service';

const UpdateAppointmentModal = ({ appointment, onClose, onSuccess, formatDate, formatTime }) => {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatingAppointment, setUpdatingAppointment] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    loadAvailableSlots();
  }, []);
  
  const loadAvailableSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Ottieni la data corrente e calcola un range di date (oggi + 30 giorni)
      const today = new Date();
      const startDate = format(today, 'yyyy-MM-dd');
      const endDate = format(addDays(today, 30), 'yyyy-MM-dd');
      
      // Ottieni le disponibilità del terapeuta
      const therapistId = appointment.therapist_user_id || appointment.therapist_id;
      const response = await DoctorCalendarService.getAvailabilities(
        therapistId, 
        startDate, 
        endDate
      );
      
      // Imposta gli slot disponibili
      const slots = response.available_slots || [];
      setAvailableSlots(slots);
      
      // Raggruppa gli slot per data e seleziona la prima data disponibile
      const uniqueDates = Array.from(new Set(slots.map(slot => slot.date)));
      if (uniqueDates.length > 0) {
        setSelectedDate(parseISO(uniqueDates[0]));
      }
      
    } catch (err) {
      console.error('Errore nel caricamento delle disponibilità:', err);
      setError('Impossibile caricare le disponibilità. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateAppointment = async () => {
    if (!selectedDate || !selectedSlot) {
      alert('Seleziona una nuova data e ora per l\'appuntamento');
      return;
    }
    
    try {
      setUpdatingAppointment(true);
      
      // Chiama il servizio per aggiornare data e ora
      await AppointmentService.updateDateTime(
        appointment.id,
        format(selectedDate, 'yyyy-MM-dd'),
        selectedSlot
      );
      
      // Notifica il componente padre che l'aggiornamento è avvenuto con successo
      onSuccess();
      
    } catch (error) {
      console.error('Errore durante l\'aggiornamento dell\'appuntamento:', error);
      alert('Impossibile aggiornare l\'appuntamento. Riprova più tardi.');
    } finally {
      setUpdatingAppointment(false);
    }
  };
  
  // Funzione per formattare la data in modo leggibile
  const formatCalendarDate = (date) => {
    return format(date, 'EEEE, d MMM', { locale: it });
  };
  
  // Funzione per verificare se due date sono lo stesso giorno
  const isSameDay = (date1, date2) => {
    return date1 && date2 && format(date1, 'yyyy-MM-dd') === format(date2, 'yyyy-MM-dd');
  };
  
  // Raggruppa gli slot per data
  const groupSlotsByDate = () => {
    const result = {};
    
    const slotsForSelectedDate = availableSlots.filter(slot => {
      return selectedDate && format(parseISO(slot.date), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
    });
    
    if (selectedDate) {
      const dateKey = format(selectedDate, 'yyyy-MM-dd');
      result[dateKey] = slotsForSelectedDate;
    }
    
    return result;
  };
  
  // Estrai le date uniche dagli slot disponibili
  const uniqueDates = Array.from(new Set(availableSlots.map(slot => slot.date)))
    .map(dateStr => parseISO(dateStr))
    .sort((a, b) => a.getTime() - b.getTime());
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">Modifica appuntamento</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h4 className="font-medium text-gray-700 mb-2">Appuntamento attuale</h4>
          <p className="text-gray-700 mb-1">
            <span className="font-medium">Paziente:</span> {appointment.patient_name || `Paziente #${appointment.patient_id}`}
          </p>
          <p className="text-gray-700 mb-1">
            <span className="font-medium">Data:</span> {formatDate(appointment.date)}
          </p>
          <p className="text-gray-700 mb-1">
            <span className="font-medium">Ora:</span> {formatTime(appointment.time)}
          </p>
          <p className="text-gray-700">
            <span className="font-medium">Servizio:</span> {appointment.service_name}
          </p>
        </div>
        
        {loading ? (
          <div className="text-center py-10">
            <svg className="animate-spin h-10 w-10 text-purple-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-3 text-gray-700">Caricamento disponibilità...</p>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        ) : (
          <div className="mb-6">
            <h4 className="font-medium text-gray-700 mb-4">Seleziona una nuova data e ora</h4>
            
            {uniqueDates.length === 0 ? (
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                <p>Non ci sono disponibilità nel prossimo periodo. Prova a controllare più tardi.</p>
              </div>
            ) : (
              <>
                {/* Selettore di data */}
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-gray-600 mb-3">Data disponibile</h5>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {uniqueDates.slice(0, 16).map((date, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          setSelectedDate(date);
                          setSelectedSlot(''); // Reset selected slot when date changes
                        }}
                        className={`p-3 rounded-md cursor-pointer border transition-colors ${
                          isSameDay(date, selectedDate) 
                            ? 'bg-purple-100 border-purple-300 text-purple-800' 
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {formatCalendarDate(date)}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Selettore di orario */}
                {selectedDate && (
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-600 mb-3">Orario disponibile</h5>
                    
                    {Object.entries(groupSlotsByDate()).map(([dateStr, slots]) => (
                      <div key={dateStr} className="flex flex-wrap gap-2">
                        {slots.length === 0 ? (
                          <p className="text-gray-500 italic">Nessun orario disponibile per questa data</p>
                        ) : (
                          slots.map(slot => (
                            <button
                              key={`${slot.date}-${slot.start}`}
                              onClick={() => setSelectedSlot(slot.start)}
                              className={`py-2 px-4 rounded transition-colors ${
                                selectedSlot === slot.start 
                                  ? 'bg-purple-600 text-white' 
                                  : 'bg-gray-100 hover:bg-gray-200'
                              }`}
                            >
                              {slot.start}
                            </button>
                          ))
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Riepilogo selezione */}
                {selectedDate && selectedSlot && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-100 mb-6">
                    <h5 className="font-medium text-green-800 mb-2">Nuovo appuntamento</h5>
                    <p className="text-gray-700 mb-1">
                      <span className="font-medium">Data:</span> {formatCalendarDate(selectedDate)}
                    </p>
                    <p className="text-gray-700 mb-1">
                      <span className="font-medium">Ora:</span> {selectedSlot}
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Servizio:</span> {appointment.service_name}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            disabled={updatingAppointment}
          >
            Annulla
          </button>
          
          <button
            onClick={handleUpdateAppointment}
            disabled={loading || updatingAppointment || !selectedDate || !selectedSlot}
            className={`px-4 py-2 rounded flex items-center ${
              loading || updatingAppointment || !selectedDate || !selectedSlot
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            {updatingAppointment ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Aggiornamento...
              </>
            ) : (
              'Aggiorna appuntamento'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

UpdateAppointmentModal.propTypes = {
  appointment: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  formatTime: PropTypes.func.isRequired
};

export default UpdateAppointmentModal;